import { useState } from "react";
import { canadaOverseasProducts, canadaOverseasCategories, canadaWarehouseLocations } from "@/mocks/canadaOverseasProducts";

type Product = typeof canadaOverseasProducts[0];

const CanadaOrderModal = ({ product, onClose }: { product: Product; onClose: () => void }) => {
  const [step, setStep] = useState<"details" | "form" | "success">("details");
  const [qty, setQty] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", province: "", postal: "" });

  const subtotal = product.price * qty;
  const commission = product.commission * qty;
  const total = subtotal;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep("success");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center bg-red-50 rounded-lg">
              <i className="ri-shopping-bag-line text-[#C41E3A]"></i>
            </div>
            <span className="font-bold text-gray-800 text-sm">Place Order — 🇨🇦 Canada Warehouse</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {step === "details" && (
          <div className="p-6">
            <div className="flex gap-4 mb-5">
              <img src={product.image} alt={product.name} className="w-20 h-20 rounded-lg object-cover object-top flex-shrink-0" />
              <div>
                <div className="font-semibold text-gray-800 text-sm leading-snug mb-1">{product.name}</div>
                <div className="text-xs text-gray-500 mb-2">{product.supplier} · {product.location}</div>
                <div className="flex items-center gap-2">
                  <span className="text-[#C41E3A] font-bold text-lg">CAD ${product.price}</span>
                  <span className="text-gray-400 line-through text-xs">CAD ${product.originalPrice}</span>
                </div>
              </div>
            </div>

            {/* How it works */}
            <div className="bg-red-50 border border-red-100 rounded-lg p-4 mb-5">
              <div className="text-xs font-bold text-red-800 mb-3 flex items-center gap-1">
                <i className="ri-information-line"></i> How This Order Works
              </div>
              <div className="space-y-2">
                {[
                  { icon: "ri-shopping-cart-line", color: "text-[#C41E3A]", text: "You place the order & payment is held securely" },
                  { icon: "ri-truck-line", color: "text-blue-600", text: "Seller ships from Canada warehouse & adds tracking" },
                  { icon: "ri-inbox-line", color: "text-green-600", text: "You receive the product & inspect quality" },
                  { icon: "ri-money-dollar-circle-line", color: "text-[#D4AF37]", text: "Confirm receipt — seller receives payment" },
                ].map((s, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 ${s.color}`}>
                      <i className={`${s.icon} text-sm`}></i>
                    </div>
                    <span className="text-xs text-red-700">{s.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="flex items-center justify-between mb-5">
              <span className="text-sm font-semibold text-gray-700">Quantity</span>
              <div className="flex items-center gap-3">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <i className="ri-subtract-line text-sm"></i>
                </button>
                <span className="font-bold text-gray-800 w-8 text-center">{qty}</span>
                <button onClick={() => setQty(Math.min(product.stock, qty + 1))} className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <i className="ri-add-line text-sm"></i>
                </button>
              </div>
            </div>

            {/* Price breakdown */}
            <div className="bg-gray-50 rounded-lg p-4 mb-5 space-y-2">
              <div className="flex justify-between text-xs text-gray-600">
                <span>Subtotal ({qty} item{qty > 1 ? "s" : ""})</span>
                <span>CAD ${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>Platform fee (2% commission)</span>
                <span>CAD ${commission.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>Shipping</span>
                <span className="text-green-600 font-semibold">FREE</span>
              </div>
              <div className="border-t border-gray-200 pt-2 flex justify-between font-bold text-gray-800 text-sm">
                <span>Total</span>
                <span>CAD ${total.toFixed(2)}</span>
              </div>
            </div>

            <button onClick={() => setStep("form")} className="w-full bg-[#C41E3A] text-white font-bold py-3 rounded-lg hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap text-sm">
              Continue to Shipping Info
            </button>
          </div>
        )}

        {step === "form" && (
          <form onSubmit={handleSubmit} className="p-6">
            <div className="text-sm font-bold text-gray-800 mb-4">Shipping Information — Canada</div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Full Name *</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#C41E3A]" placeholder="Jane Smith" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Phone *</label>
                  <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#C41E3A]" placeholder="+1 416 000 0000" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Email *</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#C41E3A]" placeholder="jane@email.com" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Street Address *</label>
                <input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#C41E3A]" placeholder="456 Maple Avenue, Unit 2" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">City *</label>
                  <input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#C41E3A]" placeholder="Toronto" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Province *</label>
                  <input required value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#C41E3A]" placeholder="ON" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Postal Code *</label>
                  <input required value={form.postal} onChange={(e) => setForm({ ...form, postal: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#C41E3A]" placeholder="M5V 2T6" />
                </div>
              </div>
            </div>

            <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
              <i className="ri-shield-check-line text-green-600 text-sm mt-0.5"></i>
              <div className="text-xs text-green-700">
                <strong>Buyer Protection:</strong> Payment held securely in escrow. Released to seller only after you confirm receipt.
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button type="button" onClick={() => setStep("details")} className="flex-1 border border-gray-200 text-gray-600 font-semibold py-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-sm">
                Back
              </button>
              <button type="submit" className="flex-grow bg-[#C41E3A] text-white font-bold py-3 rounded-lg hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap text-sm">
                <i className="ri-lock-line mr-1"></i> Pay CAD ${total.toFixed(2)} Securely
              </button>
            </div>
          </form>
        )}

        {step === "success" && (
          <div className="p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-check-line text-3xl text-green-600"></i>
            </div>
            <div className="font-bold text-gray-800 text-lg mb-2">Order Placed Successfully! 🇨🇦</div>
            <div className="text-gray-500 text-sm mb-4">
              Your order is confirmed. The seller will ship from our Canada warehouse and add a tracking number within 1-3 business days.
            </div>
            <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-left mb-5">
              <div className="text-xs font-bold text-red-800 mb-2">What happens next?</div>
              <div className="space-y-1.5">
                {[
                  "Seller ships from Canada warehouse & uploads tracking",
                  "You receive tracking info via email",
                  "Inspect your package upon delivery",
                  "Confirm receipt — seller gets paid",
                ].map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-red-700">
                    <span className="w-4 h-4 flex items-center justify-center bg-red-200 rounded-full text-[10px] font-bold flex-shrink-0">{i + 1}</span>
                    {s}
                  </div>
                ))}
              </div>
            </div>
            <button onClick={onClose} className="w-full bg-[#C41E3A] text-white font-bold py-3 rounded-lg hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap text-sm">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const CanadaMembershipModal = ({ onClose }: { onClose: () => void }) => {
  const [selectedPlan, setSelectedPlan] = useState<"credit" | "debit" | "paypal">("credit");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🇨🇦</span>
            <span className="font-bold text-gray-800 text-sm">Canada Warehouse Supplier Membership</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-6">
          <div className="text-center mb-6">
            <div className="text-5xl font-extrabold text-[#C41E3A] mb-1">CAD $8</div>
            <div className="text-gray-500 text-sm">per month · Cancel anytime</div>
            <div className="mt-3 inline-flex items-center gap-1.5 bg-green-100 text-green-700 text-xs px-4 py-1.5 rounded-full font-bold">
              <i className="ri-gift-line"></i> 3 Months FREE — Then CAD $8/month
            </div>
            <div className="text-[11px] text-gray-400 mt-2">No credit card charged for the first 3 months</div>
          </div>

          <div className="space-y-3 mb-6">
            {[
              { icon: "ri-upload-cloud-line", text: "Upload unlimited products to Canada Local Warehouse listings" },
              { icon: "ri-shopping-bag-line", text: "Receive direct orders from Canada & USA buyers" },
              { icon: "ri-money-dollar-circle-line", text: "Get paid after buyer confirms receipt (secure escrow)" },
              { icon: "ri-truck-line", text: "Add tracking numbers directly from your dashboard" },
              { icon: "ri-shield-star-line", text: "Verified Canada Supplier badge on all listings" },
              { icon: "ri-bar-chart-line", text: "Sales analytics & order management dashboard" },
              { icon: "ri-customer-service-2-line", text: "Priority support from Crown Prime Mall team" },
            ].map((b, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-7 h-7 flex items-center justify-center bg-[#C41E3A]/10 rounded-lg flex-shrink-0">
                  <i className={`${b.icon} text-[#C41E3A] text-sm`}></i>
                </div>
                <span className="text-sm text-gray-700">{b.text}</span>
              </div>
            ))}
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5 text-xs text-amber-700">
            <strong>Platform Commission:</strong> 2% of unit price per order is charged as platform fee. Covers buyer protection, payment processing & platform maintenance.
          </div>

          <div className="mb-5">
            <div className="text-xs font-bold text-gray-700 mb-3">Select Payment Method</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: "credit" as const, label: "Credit Card", icon: "ri-bank-card-line", sub: "Visa / MC / Amex" },
                { id: "debit" as const, label: "Debit Card", icon: "ri-bank-card-2-line", sub: "All major banks" },
                { id: "paypal" as const, label: "PayPal", icon: "ri-paypal-line", sub: "Fast & secure" },
              ].map((m) => (
                <button
                  key={m.id}
                  onClick={() => setSelectedPlan(m.id)}
                  className={`border-2 rounded-lg p-3 text-center cursor-pointer transition-all ${selectedPlan === m.id ? "border-[#C41E3A] bg-[#C41E3A]/5" : "border-gray-200 hover:border-gray-300"}`}
                >
                  <div className={`w-6 h-6 flex items-center justify-center mx-auto mb-1 ${selectedPlan === m.id ? "text-[#C41E3A]" : "text-gray-500"}`}>
                    <i className={`${m.icon} text-lg`}></i>
                  </div>
                  <div className={`text-xs font-semibold ${selectedPlan === m.id ? "text-[#C41E3A]" : "text-gray-700"}`}>{m.label}</div>
                  <div className="text-[10px] text-gray-400">{m.sub}</div>
                </button>
              ))}
            </div>
          </div>

          <button className="w-full bg-[#C41E3A] text-white font-bold py-3 rounded-lg hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap text-sm mb-3">
            <i className="ri-gift-line mr-1"></i> Start 3 Months Free — Then CAD $8/month
          </button>
          <div className="flex items-center justify-center gap-1 text-[10px] text-gray-400">
            <i className="ri-shield-check-line"></i>
            <span>SSL Encrypted · Cancel anytime · No hidden fees</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const CanadaProductCard = ({ product, onOrder }: { product: Product; onOrder: (p: Product) => void }) => (
  <div className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-[#C41E3A]/30 transition-all group cursor-pointer">
    <div className="relative h-44 overflow-hidden">
      <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
      <div className={`absolute top-2 left-2 ${product.badgeColor} text-white text-[10px] font-bold px-2 py-0.5 rounded-full`}>
        {product.badge}
      </div>
      <div className="absolute top-2 right-2 bg-white/90 text-gray-700 text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
        <span>🇨🇦</span> Canada Stock
      </div>
    </div>
    <div className="p-4">
      <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">{product.category}</div>
      <div className="font-semibold text-gray-800 text-sm leading-snug mb-2 line-clamp-2">{product.name}</div>
      <div className="flex items-center gap-1 mb-2">
        <div className="flex">
          {[1,2,3,4,5].map((s) => (
            <i key={s} className={s <= Math.floor(product.rating) ? "ri-star-fill text-[#D4AF37] text-xs" : "ri-star-line text-[#D4AF37] text-xs"}></i>
          ))}
        </div>
        <span className="text-xs text-gray-500">({product.reviews})</span>
        <span className="text-xs text-gray-400 ml-1">{product.sold.toLocaleString()} sold</span>
      </div>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[#C41E3A] font-extrabold text-lg">CAD ${product.price}</span>
        <span className="text-gray-400 line-through text-xs">${product.originalPrice}</span>
        <span className="bg-green-100 text-green-700 text-[10px] font-bold px-1.5 py-0.5 rounded">
          -{Math.round((1 - product.price / product.originalPrice) * 100)}%
        </span>
      </div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1 text-xs">
          <i className="ri-truck-line text-green-600"></i>
          <span className="text-green-600 font-semibold">Ships in {product.shipsIn}</span>
        </div>
        <div className="text-xs text-gray-400">{product.stock} in stock</div>
      </div>
      <div className="text-[10px] text-gray-400 mb-3 flex items-center gap-1">
        <i className="ri-map-pin-line"></i> {product.location}
      </div>
      <button
        onClick={() => onOrder(product)}
        className="w-full bg-[#C41E3A] text-white font-bold py-2.5 rounded-lg hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap text-sm flex items-center justify-center gap-2"
      >
        <i className="ri-shopping-bag-line"></i> Order Now
      </button>
    </div>
  </div>
);

const CanadaOverseasWarehouseSection = () => {
  const [activeCategory, setActiveCategory] = useState("All Products");
  const [orderProduct, setOrderProduct] = useState<Product | null>(null);
  const [showMembership, setShowMembership] = useState(false);

  const filtered = activeCategory === "All Products"
    ? canadaOverseasProducts
    : canadaOverseasProducts.filter((p) => p.category === activeCategory);

  return (
    <section id="canada-overseas-warehouse" className="py-16 bg-white">
      <div className="max-w-[1400px] mx-auto px-6">

        {/* Section Title */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-3xl">🇨🇦</span>
              <div className="bg-[#C41E3A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                Canada Local Overseas Warehouse
              </div>
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-2">
              Products Shipping From
              <span className="text-[#C41E3A]"> Canada Local Warehouse</span>
            </h2>
            <p className="text-gray-500 text-sm max-w-2xl leading-relaxed">
              Overseas suppliers stock products in our Canada warehouses. Buy directly — fast 1-5 day delivery across Canada, no import duties, buyer-protected payments. Seller only gets paid after you confirm receipt.
            </p>
          </div>

          {/* Supplier CTA Card */}
          <div className="flex-shrink-0">
            <div className="bg-white border-2 border-[#C41E3A] rounded-xl p-5 text-center min-w-[240px]">
              <div className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-[11px] font-bold px-3 py-1 rounded-full mb-2">
                <i className="ri-gift-line"></i> 3 Months FREE
              </div>
              <div className="text-[#C41E3A] font-extrabold text-3xl mb-0.5">CAD $8</div>
              <div className="text-gray-500 text-xs mb-3">/ month after free period</div>
              <button
                onClick={() => setShowMembership(true)}
                className="w-full bg-[#C41E3A] text-white font-bold py-2.5 rounded-lg hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-store-2-line mr-1"></i> List Your Products
              </button>
              <div className="text-[10px] text-gray-400 mt-2">+ 2% commission per order</div>
            </div>
          </div>
        </div>

        {/* 3 MONTHS FREE — Big Centered Supplier Attraction Banner */}
        <div className="relative rounded-2xl overflow-hidden mb-10" style={{ background: "linear-gradient(135deg, #8B0000 0%, #C41E3A 45%, #e8354f 70%, #C41E3A 100%)" }}>
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 0, transparent 50%)", backgroundSize: "12px 12px" }} />
          <div className="relative z-10 py-10 px-6 flex flex-col items-center text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 border border-white/40 text-white text-xs font-bold px-4 py-1.5 rounded-full mb-4 uppercase tracking-widest">
              <i className="ri-store-2-line"></i> 🇨🇦 Canada Warehouse Supplier Offer
            </div>
            <div className="text-white font-black text-5xl md:text-7xl lg:text-8xl leading-none mb-3 drop-shadow-lg" style={{ textShadow: "0 4px 24px rgba(0,0,0,0.3)" }}>
              3 MONTHS
            </div>
            <div className="bg-white text-[#C41E3A] font-black text-4xl md:text-6xl lg:text-7xl px-8 py-2 rounded-2xl mb-4 leading-none shadow-lg">
              FREE
            </div>
            <p className="text-white/90 text-base md:text-xl font-semibold mb-2 max-w-2xl">
              List your products in our <strong className="text-white">Canada Local Warehouses</strong> — No charge for the first 3 months!
            </p>
            <p className="text-white/70 text-sm mb-8 max-w-xl">
              After your free period, only <strong className="text-white">CAD $8/month</strong> + 2% commission per order. Cancel anytime. No hidden fees.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {[
                { icon: "ri-upload-cloud-line", text: "Upload Unlimited Products" },
                { icon: "ri-shield-check-line", text: "Verified Supplier Badge" },
                { icon: "ri-money-dollar-circle-line", text: "Secure Escrow Payments" },
                { icon: "ri-truck-line", text: "Canada Local Fulfillment" },
                { icon: "ri-bar-chart-line", text: "Sales Dashboard" },
                { icon: "ri-customer-service-2-line", text: "Priority Support" },
              ].map((f) => (
                <div key={f.text} className="flex items-center gap-2 bg-white/15 border border-white/30 text-white text-xs font-semibold px-4 py-2 rounded-full">
                  <i className={`${f.icon}`}></i>
                  {f.text}
                </div>
              ))}
            </div>
            <button
              onClick={() => setShowMembership(true)}
              className="bg-white text-[#C41E3A] font-black text-lg px-12 py-4 rounded-full hover:bg-red-50 transition-all cursor-pointer whitespace-nowrap shadow-xl hover:scale-105"
            >
              <i className="ri-gift-line mr-2"></i> Claim Your 3 Months FREE Now
            </button>
            <div className="flex items-center gap-2 mt-4 text-white/60 text-xs">
              <i className="ri-shield-check-line"></i>
              <span>SSL Encrypted · No credit card charged for 3 months · Cancel anytime</span>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-[#fafafa] border border-gray-100 rounded-xl p-5 mb-8">
          <div className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-4 text-center">
            How It Works — 🇨🇦 Canada Buyer Protection Guarantee
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { step: "1", icon: "ri-shopping-cart-line", color: "bg-[#C41E3A]/10 text-[#C41E3A]", title: "Place Order", desc: "Browse & buy. Payment held securely in escrow." },
              { step: "2", icon: "ri-truck-line", color: "bg-blue-50 text-blue-600", title: "Seller Ships", desc: "Seller ships from Canada warehouse & adds tracking." },
              { step: "3", icon: "ri-inbox-line", color: "bg-green-50 text-green-600", title: "You Receive", desc: "Get your package in 1-5 days. Inspect quality." },
              { step: "4", icon: "ri-money-dollar-circle-line", color: "bg-[#D4AF37]/10 text-[#D4AF37]", title: "Confirm & Pay", desc: "Confirm receipt — seller gets paid. You're protected." },
            ].map((s) => (
              <div key={s.step} className="flex flex-col items-center text-center">
                <div className={`w-12 h-12 flex items-center justify-center rounded-full ${s.color} mb-3`}>
                  <i className={`${s.icon} text-xl`}></i>
                </div>
                <div className="font-bold text-gray-800 text-sm mb-1">{s.title}</div>
                <div className="text-xs text-gray-500 leading-relaxed">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Warehouse Locations */}
        <div className="flex flex-wrap gap-2 mb-6">
          <span className="text-xs font-bold text-gray-600 flex items-center gap-1 mr-2">
            <i className="ri-map-pin-line text-[#C41E3A]"></i> Canada Warehouse Locations:
          </span>
          {canadaWarehouseLocations.map((loc) => (
            <span key={loc.city} className="bg-[#fafafa] border border-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full flex items-center gap-1">
              🇨🇦 {loc.city}, {loc.province}
              <span className="text-gray-400">· {loc.products.toLocaleString()} items</span>
            </span>
          ))}
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {canadaOverseasCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? "bg-[#C41E3A] text-white"
                  : "bg-[#fafafa] border border-gray-200 text-gray-600 hover:border-[#C41E3A] hover:text-[#C41E3A]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mb-10" data-product-shop>
          {filtered.map((product) => (
            <CanadaProductCard key={product.id} product={product} onOrder={setOrderProduct} />
          ))}
        </div>

        {/* Bottom CTA for Suppliers */}
        <div className="bg-gradient-to-r from-[#8B0000] to-[#C41E3A] rounded-xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">🇨🇦</span>
              <div className="text-white/80 text-xs font-bold uppercase tracking-wider">Are You an Overseas Supplier?</div>
            </div>
            <h3 className="text-white font-extrabold text-xl md:text-2xl mb-2">
              List Your Products in Canada Warehouses
            </h3>
            <p className="text-white/70 text-sm max-w-lg leading-relaxed">
              Join verified overseas suppliers selling directly to Canada & USA buyers. Stock your products in our local Canadian warehouses and reach millions of customers. <strong className="text-[#D4AF37]">First 3 months FREE</strong>, then only <strong className="text-[#D4AF37]">CAD $8/month</strong> + 2% commission per order.
            </p>
            <div className="flex flex-wrap gap-4 mt-4">
              {[
                { icon: "ri-shield-check-line", text: "Verified Supplier Badge" },
                { icon: "ri-money-dollar-circle-line", text: "Secure Escrow Payments" },
                { icon: "ri-truck-line", text: "Canada Local Fulfillment" },
                { icon: "ri-bar-chart-line", text: "Sales Dashboard" },
              ].map((f) => (
                <div key={f.text} className="flex items-center gap-1.5 text-white/80 text-xs">
                  <i className={`${f.icon} text-[#D4AF37]`}></i>
                  {f.text}
                </div>
              ))}
            </div>
          </div>
          <div className="flex-shrink-0 text-center">
            <div className="inline-flex items-center gap-1 bg-green-500/20 text-green-300 text-xs font-bold px-3 py-1 rounded-full mb-2">
              <i className="ri-gift-line"></i> 3 Months FREE
            </div>
            <div className="text-[#D4AF37] font-extrabold text-4xl mb-0.5">CAD $8</div>
            <div className="text-white/60 text-xs mb-4">per month after free period</div>
            <button
              onClick={() => setShowMembership(true)}
              className="bg-white text-[#C41E3A] font-bold px-8 py-3 rounded-full hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-store-2-line mr-1"></i> Join as Supplier
            </button>
            <div className="flex items-center justify-center gap-3 mt-3">
              {["VISA", "MC", "PayPal"].map((p) => (
                <span key={p} className="bg-white/10 text-white/70 text-[10px] px-2 py-0.5 rounded font-bold border border-white/20">
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {orderProduct && <CanadaOrderModal product={orderProduct} onClose={() => setOrderProduct(null)} />}
      {showMembership && <CanadaMembershipModal onClose={() => setShowMembership(false)} />}
    </section>
  );
};

export default CanadaOverseasWarehouseSection;
